import type { TFunction } from 'i18next'
import { intlLocale } from '~/i18n/format'

/** A number with `digits` decimals in the panel language's marks: `96.12`. */
export function formatFixed(value: number, digits: number): string {
  return value.toLocaleString(intlLocale(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

/** An RTP (a fraction) as a percentage with two decimals; a dash when unknown. */
export function formatRtp(rtp: number | null): string {
  return rtp === null ? '—' : `${formatFixed(rtp * 100, 2)}%`
}

/**
 * An RTP minus the one it is compared with (fractions): a draft's against
 * the current version's, a simulated one against the analytic one. Null
 * without both.
 */
export function rtpDeviation(
  rtp: number | null,
  other: number | null,
): number | null {
  return rtp === null || other === null ? null : rtp - other
}

/**
 * A deviation (a fraction) in signed percentage points: `+1.25 pp`. Without
 * `t` the unit reads `pp`.
 */
export function formatRtpDeviation(deviation: number, t?: TFunction): string {
  const points = formatFixed(Math.abs(deviation) * 100, 2)
  const zero = (Math.abs(deviation) * 100).toFixed(2) === '0.00'
  let sign = deviation > 0 ? '+' : '−'
  if (zero) {
    sign = '±'
  }
  return t === undefined
    ? `${sign}${points} pp`
    : t('simulationPage.format.deviation', { sign, points })
}

/** The N of "1 in N paid spins": a whole number from 100, one decimal below. */
export function formatTriggerOdds(oneIn: number): string {
  return oneIn >= 100
    ? formatFixed(Math.round(oneIn), 0)
    : formatFixed(oneIn, 1)
}
